// Sign-in for the browser apps (the staff app and the portal): the only code
// that knows the sign-in provider is Cognito (CLAUDE.md: vendor services behind
// thin interfaces). It calls Cognito's public, unsigned sign-in API with fetch
// rather than bundling the AWS SDK. Each app passes its own pool's client id.
//
// Two ways in: an emailed one-time code (Cognito sends it through SES from
// staff-smith.com, QUESTIONS.md Q23) or a password. While SES is in its sandbox
// (Q24) codes reach only verified addresses, so the staff app keeps the password.
// Portal users have only codes. Operators use a password and an authenticator
// app's code, always (QUESTIONS.md Q49), or a passkey they've added.
//
// Tokens live in sessionStorage: they last for the browser tab and are gone when
// it closes. The access token is what the API checks; it expires after an hour
// and is renewed with the refresh token (12 hours).

export type Tokens = { accessToken: string; refreshToken: string; expiresAt: number };

export class SignInError extends Error {}

// After an operator's password: an authenticator code, or (first sign-in) setting
// the authenticator up with the secret Cognito issued.
export type AuthenticatorStep =
  | { kind: "code"; session: string }
  | { kind: "setup"; session: string; secret: string; uri: string };

// The browser's passkey prompts, taking and returning WebAuthn's JSON forms
// (what Cognito sends and expects). Replaced in tests.
export type PasskeyDevice = {
  create(options: PublicKeyCredentialCreationOptionsJSON): Promise<unknown>;
  get(options: PublicKeyCredentialRequestOptionsJSON): Promise<unknown>;
};

export type Passkey = { id: string; name: string; createdAt: string };

// Whether this browser can make and use passkeys through the JSON helpers
// (current Chrome, Edge, Firefox and Safari).
export const passkeysSupported = () =>
  typeof PublicKeyCredential !== "undefined" &&
  typeof PublicKeyCredential.parseRequestOptionsFromJSON === "function";

const browserPasskeys: PasskeyDevice = {
  async create(options) {
    const credential = await navigator.credentials.create({
      publicKey: PublicKeyCredential.parseCreationOptionsFromJSON(options),
    });
    if (!credential) throw new SignInError("No passkey was made.");
    return (credential as PublicKeyCredential).toJSON();
  },
  async get(options) {
    const credential = await navigator.credentials.get({
      publicKey: PublicKeyCredential.parseRequestOptionsFromJSON(options),
    });
    if (!credential) throw new SignInError("No passkey was chosen.");
    return (credential as PublicKeyCredential).toJSON();
  },
};

type Deps = {
  region: string;
  clientId: string;
  // Where this app keeps its tokens, so two apps never share a sign-in.
  storageKey?: string;
  fetch?: typeof fetch;
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  now?: () => number;
  passkeys?: PasskeyDevice;
};

type AuthResult = { AccessToken: string; RefreshToken?: string; ExpiresIn: number };
type CognitoResponse = {
  AuthenticationResult?: AuthResult;
  ChallengeName?: string;
  ChallengeParameters?: Record<string, string>;
  AvailableChallenges?: string[];
  Session?: string;
};

export function createAuth(deps: Deps) {
  const doFetch = deps.fetch ?? fetch.bind(globalThis);
  const storage = deps.storage ?? sessionStorage;
  const now = deps.now ?? Date.now;
  const key = deps.storageKey ?? "staffsmith.tokens";
  const endpoint = `https://cognito-idp.${deps.region}.amazonaws.com/`;
  const passkeys = deps.passkeys ?? browserPasskeys;

  // withClient: false for the calls that take only a session or an access token
  // (authenticator set-up, passkeys).
  async function cognito(
    action: string,
    body: Record<string, unknown>,
    withClient = true,
  ): Promise<CognitoResponse> {
    const res = await doFetch(endpoint, {
      method: "POST",
      headers: {
        "content-type": "application/x-amz-json-1.1",
        "x-amz-target": `AWSCognitoIdentityProviderService.${action}`,
      },
      body: JSON.stringify(withClient ? { ClientId: deps.clientId, ...body } : body),
    });
    const data = (await res.json().catch(() => ({}))) as CognitoResponse & {
      __type?: string;
      message?: string;
    };
    if (!res.ok) {
      // Cognito's own wording, minus the type prefix; never the password.
      const kind = (data.__type ?? "").split("#").pop() ?? "";
      throw new SignInError(
        kind === "NotAuthorizedException" || kind === "UserNotFoundException"
          ? "That email and password don't match."
          : (data.message ?? `Sign-in failed (${res.status})`),
      );
    }
    return data;
  }

  function save(result: AuthResult, refreshToken?: string): Tokens {
    const tokens: Tokens = {
      accessToken: result.AccessToken,
      refreshToken: result.RefreshToken ?? refreshToken ?? "",
      // Renew a minute early, so a request never leaves with a token about to lapse.
      expiresAt: now() + (result.ExpiresIn - 60) * 1000,
    };
    storage.setItem(key, JSON.stringify(tokens));
    return tokens;
  }

  function stored(): Tokens | undefined {
    try {
      const raw = storage.getItem(key);
      return raw ? (JSON.parse(raw) as Tokens) : undefined;
    } catch {
      return undefined;
    }
  }

  // A current access token, renewed if it has lapsed; undefined when signed out.
  async function accessToken(): Promise<string | undefined> {
    const tokens = stored();
    if (!tokens) return undefined;
    if (tokens.expiresAt > now()) return tokens.accessToken;
    if (!tokens.refreshToken) return undefined;
    try {
      const r = await cognito("InitiateAuth", {
        AuthFlow: "REFRESH_TOKEN_AUTH",
        AuthParameters: { REFRESH_TOKEN: tokens.refreshToken },
      });
      if (!r.AuthenticationResult) return undefined;
      return save(r.AuthenticationResult, tokens.refreshToken).accessToken;
    } catch {
      storage.removeItem(key);
      return undefined;
    }
  }

  async function signedInToken(): Promise<string> {
    const token = await accessToken();
    if (!token) throw new SignInError("Sign in again first.");
    return token;
  }

  return {
    async signInWithPassword(email: string, password: string): Promise<Tokens> {
      const r = await cognito("InitiateAuth", {
        AuthFlow: "USER_AUTH",
        AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: "PASSWORD", PASSWORD: password },
      });
      if (!r.AuthenticationResult) {
        throw new SignInError(
          `Sign-in needs a step this app doesn't support yet (${r.ChallengeName ?? "unknown"}).`,
        );
      }
      return save(r.AuthenticationResult);
    },

    // Step one of a one-time code sign-in: Cognito emails the code.
    async startEmailCode(email: string): Promise<string> {
      const r = await cognito("InitiateAuth", {
        AuthFlow: "USER_AUTH",
        AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: "EMAIL_OTP" },
      });
      if (r.ChallengeName !== "EMAIL_OTP" || !r.Session) {
        throw new SignInError("Email codes aren't available for this account.");
      }
      return r.Session;
    },

    async confirmEmailCode(email: string, session: string, code: string): Promise<Tokens> {
      const r = await cognito("RespondToAuthChallenge", {
        ChallengeName: "EMAIL_OTP",
        Session: session,
        ChallengeResponses: { USERNAME: email, EMAIL_OTP_CODE: code },
      });
      if (!r.AuthenticationResult) throw new SignInError("That code didn't work.");
      return save(r.AuthenticationResult);
    },

    // Operators (QUESTIONS.md Q49): a password, then an authenticator app's code,
    // always. The first sign-in sets the authenticator up instead. Returns the
    // tokens, or the step that comes next.
    async startPasswordWithAuthenticator(
      email: string,
      password: string,
    ): Promise<AuthenticatorStep> {
      const r = await cognito("InitiateAuth", {
        AuthFlow: "USER_AUTH",
        AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: "PASSWORD", PASSWORD: password },
      });
      if (r.ChallengeName === "SOFTWARE_TOKEN_MFA" && r.Session) {
        return { kind: "code", session: r.Session };
      }
      if (r.ChallengeName === "MFA_SETUP" && r.Session) {
        // Cognito issues the authenticator's secret for this sign-in only.
        const a = (await cognito(
          "AssociateSoftwareToken",
          { Session: r.Session },
          false,
        )) as CognitoResponse & {
          SecretCode?: string;
        };
        if (!a.SecretCode || !a.Session)
          throw new SignInError("Couldn't start authenticator set-up.");
        return {
          kind: "setup",
          session: a.Session,
          secret: a.SecretCode,
          // What authenticator apps read from a QR code, for apps that take a link.
          uri: `otpauth://totp/${encodeURIComponent(`StaffSmith operators:${email}`)}?secret=${a.SecretCode}&issuer=StaffSmith`,
        };
      }
      throw new SignInError(
        `Sign-in needs a step this app doesn't support yet (${r.ChallengeName ?? "unknown"}).`,
      );
    },

    // The six-digit code from the authenticator app.
    async confirmAuthenticatorCode(email: string, session: string, code: string): Promise<Tokens> {
      const r = await cognito("RespondToAuthChallenge", {
        ChallengeName: "SOFTWARE_TOKEN_MFA",
        Session: session,
        ChallengeResponses: { USERNAME: email, SOFTWARE_TOKEN_MFA_CODE: code },
      });
      if (!r.AuthenticationResult) throw new SignInError("That code didn't work.");
      return save(r.AuthenticationResult);
    },

    // First sign-in: the authenticator's first code proves it's set up, then
    // Cognito finishes the sign-in.
    async finishAuthenticatorSetup(email: string, session: string, code: string): Promise<Tokens> {
      const v = (await cognito(
        "VerifySoftwareToken",
        { Session: session, UserCode: code, FriendlyDeviceName: "authenticator" },
        false,
      )) as CognitoResponse & { Status?: string };
      if (v.Status !== "SUCCESS" || !v.Session) throw new SignInError("That code didn't work.");
      const r = await cognito("RespondToAuthChallenge", {
        ChallengeName: "MFA_SETUP",
        Session: v.Session,
        ChallengeResponses: { USERNAME: email },
      });
      if (!r.AuthenticationResult) throw new SignInError("Set-up finished, but sign-in didn't.");
      return save(r.AuthenticationResult);
    },

    // A passkey that checks who's holding it (fingerprint, face or device PIN)
    // stands in for both the password and the authenticator code. Cognito offers
    // it only once the account has one; until then it offers the password.
    async signInWithPasskey(email: string): Promise<Tokens> {
      const r = await cognito("InitiateAuth", {
        AuthFlow: "USER_AUTH",
        AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: "WEB_AUTHN" },
      });
      const options = r.ChallengeParameters?.CREDENTIAL_REQUEST_OPTIONS;
      if (r.ChallengeName !== "WEB_AUTHN" || !r.Session || !options) {
        throw new SignInError(
          "This account has no passkey yet. Sign in with your password and authenticator code, then add one.",
        );
      }
      const credential = await passkeys.get(
        JSON.parse(options) as PublicKeyCredentialRequestOptionsJSON,
      );
      const done = await cognito("RespondToAuthChallenge", {
        ChallengeName: "WEB_AUTHN",
        Session: r.Session,
        ChallengeResponses: { USERNAME: email, CREDENTIAL: JSON.stringify(credential) },
      });
      if (!done.AuthenticationResult) throw new SignInError("That passkey didn't work.");
      return save(done.AuthenticationResult);
    },

    // Adds a passkey on this device for the signed-in user.
    async addPasskey(): Promise<void> {
      const token = await signedInToken();
      const start = (await cognito("StartWebAuthnRegistration", { AccessToken: token }, false)) as {
        CredentialCreationOptions?: PublicKeyCredentialCreationOptionsJSON;
      };
      if (!start.CredentialCreationOptions)
        throw new SignInError("Couldn't start adding a passkey.");
      const credential = await passkeys.create(start.CredentialCreationOptions);
      await cognito(
        "CompleteWebAuthnRegistration",
        { AccessToken: token, Credential: credential },
        false,
      );
    },

    async listPasskeys(): Promise<Passkey[]> {
      const token = await signedInToken();
      const r = (await cognito("ListWebAuthnCredentials", { AccessToken: token }, false)) as {
        Credentials?: {
          CredentialId: string;
          FriendlyCredentialName?: string;
          CreatedAt: number;
        }[];
      };
      return (r.Credentials ?? []).map((c) => ({
        id: c.CredentialId,
        name: c.FriendlyCredentialName ?? "Passkey",
        // Cognito gives seconds since 1970.
        createdAt: new Date(c.CreatedAt * 1000).toISOString(),
      }));
    },

    async removePasskey(id: string): Promise<void> {
      const token = await signedInToken();
      await cognito("DeleteWebAuthnCredential", { AccessToken: token, CredentialId: id }, false);
    },

    accessToken,

    isSignedIn(): boolean {
      return stored() !== undefined;
    },

    signOut(): void {
      storage.removeItem(key);
    },
  };
}

export type Auth = ReturnType<typeof createAuth>;

export { PRODUCT_NAME } from "./brand.js";
export {
  importPage,
  isChunkLoadError,
  recoverFromStaleDeploy,
  STALE_DEPLOY_MESSAGE,
  watchForStaleDeploy,
} from "./stale-deploy.js";
export { wakeDatabase } from "./wake.js";
